import {
    useQuery,
    useMutation,
    QueryResult,
    UseMutationOptions,
    UseMutationResult,
    QueryOptions,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import type { CompanyInformation, ContactInfo, ManagerId, UpdateManagerPayload } from '../generated-types';
import { getCompanyInformation, updateGeneralManager } from '../services/companyService';
import { getContactInfo, getConfirmedStatus } from '../services/profileService';

export const useContactInformation = (
    queryOptions: Omit<QueryOptions<ContactInfo>, 'queryKey'> = {},
): QueryResult<ContactInfo> =>
    useQuery<ContactInfo>({
        ...queryOptions,
        queryKey: ['contact-information'],
        queryFn: getContactInfo,
    });

export const useCompanyInformation = (
    queryOptions: Omit<QueryOptions<CompanyInformation>, 'queryKey'> = {},
): QueryResult<CompanyInformation> =>
    useQuery<CompanyInformation>({
        ...queryOptions,
        queryKey: ['company-information'],
        queryFn: getCompanyInformation,
    });

export const useConfirmedStatus = (queryOptions: Omit<QueryOptions<boolean>, 'queryKey'> = {}): QueryResult<boolean> =>
    useQuery<boolean>({
        ...queryOptions,
        queryKey: ['confirmed-status'],
        queryFn: getConfirmedStatus,
    });

type UpdateGeneralManagerMutationOptions = UseMutationOptions<
    void,
    RequestExceptionWithResponse,
    UpdateManagerPayload & {
        id: ManagerId;
    }
>;

export const useUpdateGeneralManager = (
    queryOptions?: UpdateGeneralManagerMutationOptions,
): UseMutationResult<
    void,
    RequestExceptionWithResponse,
    UpdateManagerPayload & {
        id: ManagerId;
    }
> =>
    useMutation<
        void,
        RequestExceptionWithResponse,
        UpdateManagerPayload & {
            id: ManagerId;
        }
    >({
        mutationKey: ['edit-general-manager'],
        mutationFn: ({ id, ...payload }) => updateGeneralManager(id, payload),
        ...queryOptions,
    });
